/**
 * @file enums.ts
 * @description Centralized constants and types.
 * @standard Migrated from TypeScript `enum` to `as const` objects for better type inference and DX.
 */

export const UserType = {
  Mentee: 'mentees',
  Mentor: 'mentors',
  All: 'all',
  Coaching: 'coaching'
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];

export const UserRoleType = {
  Mentor: 'Mentor',
  Mentee: 'Mentee',
  Coach: 'Coach',
  Coachee: 'Coachee',
  BasicSubscription: 'Basic Subscription'
} as const;
export type UserRoleType = (typeof UserRoleType)[keyof typeof UserRoleType];

/**
 * Helper to determine if a user role has "leader" permissions (Coach or Mentor).
 */
export function isLeaderRole(type?: string): boolean {
  return type === UserRoleType.Coach || type === UserRoleType.Mentor;
}

export const SessionStageType = {
  MeetAndGreet: 'Meet And Greet',
  RecurringSession: 'Recurring Session',
  FinalSession: 'Final Session',
  Coaching: 'Coaching Session',
  Individual: 'Individual Session'
} as const;
export type SessionStageType = (typeof SessionStageType)[keyof typeof SessionStageType];

export const TaskType = {
  FirstSessionAssessment: 'First Session Assessment',
  MidCohortGroupAssessment: 'Mid Cohort Group Assessment',
  FinalGroupAssessment: 'Final Group Assessment',
  PilotGroupCoachingAssessment: 'Pilot Group Coaching Assessment',
  OneOnOneFinalSurvey: 'One on One Final Survey',
  OneOnOneMidCohortSurvey: 'One on One Mid-Cohort Survey'
} as const;
export type TaskType = (typeof TaskType)[keyof typeof TaskType];

export const SurveyTargetType = {
  All: 'all'
} as const;
export type SurveyTargetType = (typeof SurveyTargetType)[keyof typeof SurveyTargetType];

export const SurveyType = {
  OnboardingMentee: 'Onboarding Mentee',
  OnboardingMentor: 'Onboarding Mentor',
  MeetAndGreetFeedback: 'Meet and Greet Feedback',
  FinalSurvey: 'Final Survey',
  TermsAndConditionsAgreement: 'Term and Conditions Agreement',
  MidCohortSurvey: 'Mid-Cohort Survey',
  Onboarding: 'Onboarding'
} as const;
export type SurveyType = (typeof SurveyType)[keyof typeof SurveyType];

export const ResourcePlacement = {
  Homepage: 'Home Page',
  ResourcesPage: 'Resources Page'
} as const;
export type ResourcePlacement = (typeof ResourcePlacement)[keyof typeof ResourcePlacement];

export const UserStatus = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE',
  New: 'NEW'
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const GroupStatus = {
  Active: 'ACTIVE',
  Inactive: 'INACTIVE'
} as const;
export type GroupStatus = (typeof GroupStatus)[keyof typeof GroupStatus];

export const Emails = {
  MainEmail: 'team@locelle.com'
} as const;
export type Emails = (typeof Emails)[keyof typeof Emails];

export const SessionNotesTemplate = {
  GroupNotesTemplate: 'GROUP_NOTES_TEMPLATE',
  OneOnOneNotesTemplate: 'ONE_ON_ONE_NOTES_TEMPLATE'
} as const;
export type SessionNotesTemplate = (typeof SessionNotesTemplate)[keyof typeof SessionNotesTemplate];

export const SessionDurationMinutes = {
  Thirty: 30,
  FortyFive: 45,
  Sixty: 60,
  Ninety: 90,
  HundredFive: 105,
  HundredTwenty: 120
} as const;
export type SessionDurationMinutes = (typeof SessionDurationMinutes)[keyof typeof SessionDurationMinutes];
